"use client";

import React, { useRef, useMemo, useState, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@/lib/utils";

interface VirtualizedListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  itemHeight?: number;
  containerHeight?: number | string;
  className?: string;
  overscan?: number;
  // 用于标识哪些项已更新，触发重新渲染
  updateKeys?: (string | number)[];
}

/**
 * 虚拟化列表组件
 * 只渲染可见区域的项目，大幅提升大列表的渲染性能
 */
export function VirtualizedList<T>({
  items,
  renderItem,
  itemHeight = 50,
  containerHeight = 400,
  className,
  overscan = 5,
  updateKeys,
}: VirtualizedListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [actualContainerHeight, setActualContainerHeight] = useState<number | string>(
    typeof containerHeight === "number" ? containerHeight : 
    containerHeight === "100%" ? "100%" : 400
  );

  // 动态计算容器高度（仅在 containerHeight 为 "100%" 时）
  useEffect(() => {
    if (typeof containerHeight !== "string" || containerHeight !== "100%") {
      setActualContainerHeight(containerHeight);
      return;
    }

    if (!parentRef.current) return;

    // 计算实际高度
    const calculateHeight = () => {
      if (!parentRef.current) return;
      
      // 获取父容器的高度
      const parent = parentRef.current.parentElement;
      if (parent) {
        const height = parent.getBoundingClientRect().height;
        if (height > 0) {
          setActualContainerHeight(height);
        }
      }
    };

    // 初始计算（延迟一下确保 DOM 已渲染）
    const timer = setTimeout(calculateHeight, 0);

    // 使用 ResizeObserver 监听容器大小变化
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        calculateHeight();
      }
    });

    // 监听父容器和自身的大小变化
    if (parentRef.current.parentElement) {
      resizeObserver.observe(parentRef.current.parentElement);
    }
    if (parentRef.current) {
      resizeObserver.observe(parentRef.current);
    }

    // 监听窗口大小变化
    window.addEventListener('resize', calculateHeight);

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateHeight);
    };
  }, [containerHeight]);

  // 使用 useMemo 缓存虚拟化器，仅在 items 变化时重新创建
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemHeight,
    overscan,
    // 启用动态高度测量，自动计算每个元素的实际高度
    measureElement: (element) => {
      return element?.getBoundingClientRect().height ?? itemHeight;
    },
  });

  // 当 items 变化时重新计算虚拟项
  const virtualItems = useMemo(() => {
    return virtualizer.getVirtualItems();
  }, [virtualizer, items.length]);

  // 调试日志
  if (items.length > 0 && virtualItems.length === 0) {
    console.warn('[VirtualizedList] 有数据但没有虚拟项:', {
      itemsCount: items.length,
      virtualItemsCount: virtualItems.length,
      totalSize: virtualizer.getTotalSize(),
    });
  }

  return (
    <div
      ref={parentRef}
      className={cn("overflow-auto", className)}
      style={{
        height: typeof actualContainerHeight === "number" 
          ? `${actualContainerHeight}px` 
          : actualContainerHeight,
        position: "relative",
        minHeight: 0, // 确保 flex 布局正确
      }}
    >
      {items.length === 0 ? (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          暂无数据
        </div>
      ) : (
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: "100%",
            position: "relative",
          }}
        >
          {virtualItems.map((virtualItem) => {
            const item = items[virtualItem.index];
            if (!item) {
              console.warn('[VirtualizedList] 项不存在:', virtualItem.index);
              return null;
            }
            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: `${virtualItem.size}px`,
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {renderItem(item, virtualItem.index)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


